import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Phone, MapPin, Bed, Bath, Maximize, Toilet, Leaf, Sun, Wind, Facebook, Instagram, Linkedin, Youtube } from 'lucide-react'
import { motion } from 'framer-motion'
import ChatWidget from '@/components/ChatWidget'
import PropertyFilters, { FILTERS_INITIAL_STATE, PropertyFiltersState, applyFilters } from '@/components/PropertyFilters'
import PropertyPagination from '@/components/PropertyPagination'

interface Property {
  id: string
  seo_title: string
  title: string
  description: string
  price: number
  currency: string
  property_type: string
  purpose: string
  bedrooms?: number
  bathrooms?: number
  half_bathrooms?: number
  area_m2?: number
  zone: string
  city?: string
  amenities?: string[]
  featured_image?: string
}

interface NaturalTemplateProps {
  agent: {
    name: string
    bio: string
    phone: string
    specialties: string[]
    zones: string[]
    avatar_url?: string
    social_media?: {
      facebook?: string
      instagram?: string
      linkedin?: string
      tiktok?: string
      youtube?: string
    }
  }
}

const NaturalTemplate: React.FC<NaturalTemplateProps> = ({ agent }) => {
  const navigate = useNavigate()
  const [properties, setProperties] = useState<Property[]>([])
  const [filters, setFilters] = useState<PropertyFiltersState>(FILTERS_INITIAL_STATE)
  const [showFilters, setShowFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(6)

  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const subdomain = window.location.hostname.split('.')[0]
        const response = await fetch(`/api/agents/subdomain/${subdomain}/properties`)
        if (response.ok) {
          const data = await response.json()
          setProperties(data)
        }
      } catch (error) {
        console.error('Error fetching properties:', error)
      }
    }
    fetchProperties()
  }, [])

  useEffect(() => {
    setCurrentPage(1)
  }, [filters])

  const formatPrice = (price: number, currency: string) => {
    return `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} ${currency}`
  }

  const handlePropertyClick = (seoTitle: string) => {
    navigate(`/property/${seoTitle}`)
  }

  const filteredProperties = applyFilters(properties, filters)
  const propertyTypes = [...new Set(properties.map(p => p.property_type).filter(Boolean))]
  const totalPages = perPage === 0 ? 1 : Math.ceil(filteredProperties.length / perPage)
  const paginatedProperties = perPage === 0
    ? filteredProperties
    : filteredProperties.slice((currentPage - 1) * perPage, currentPage * perPage)
  const showPhone = false
  const showWhatsapp = false

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F0E8', fontFamily: "'Georgia', serif" }}>

      {/* Header orgánico */}
      <header className="sticky top-0 z-50 border-b" style={{ backgroundColor: '#F5F0E8', borderColor: '#D4C5A9' }}>
        <div className="max-w-6xl mx-auto px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Leaf size={20} style={{ color: '#4A7C59' }} />
            <span className="font-light tracking-wide" style={{ color: '#3D3228' }}>{agent.name}</span>
          </div>
          {agent.phone && (
            <a href={`tel:${agent.phone}`}
              className="text-sm font-light transition-opacity hover:opacity-70"
              style={{ color: '#4A7C59' }}>
              {agent.phone}
            </a>
          )}
        </div>
      </header>

      {/* Hero — naturaleza y espacio */}
      <section className="relative overflow-hidden py-24 lg:py-32">
        {/* Decoración orgánica */}
        <div className="absolute top-0 right-0 w-96 h-96 opacity-10 pointer-events-none">
          <svg viewBox="0 0 400 400" fill="none">
            <circle cx="300" cy="100" r="200" fill="#4A7C59" />
            <circle cx="350" cy="150" r="150" fill="#7BAF8A" />
          </svg>
        </div>
        <div className="absolute bottom-0 left-0 w-64 h-64 opacity-10 pointer-events-none">
          <svg viewBox="0 0 300 300" fill="none">
            <circle cx="50" cy="250" r="180" fill="#8B7355" />
          </svg>
        </div>

        <div className="relative max-w-6xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">

            {/* Texto */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: 'easeOut' }}
              className="space-y-8"
            >
              <div className="flex items-center gap-2">
                <div className="h-px w-8" style={{ backgroundColor: '#4A7C59' }} />
                <span className="text-xs tracking-widest uppercase" style={{ color: '#4A7C59' }}>
                  Vida Natural · Espacios con Alma
                </span>
              </div>

              <h1 className="text-5xl lg:text-6xl font-light leading-tight" style={{ color: '#3D3228' }}>
                {agent.name}
              </h1>

              {agent.bio && (
                <p className="text-lg leading-relaxed font-light" style={{ color: '#6B5744' }}>
                  {agent.bio}
                </p>
              )}

              {agent.zones?.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <MapPin size={16} style={{ color: '#4A7C59' }} />
                  {agent.zones.slice(0, 3).map((z, i) => (
                    <span key={i} className="text-sm font-light" style={{ color: '#8B7355' }}>
                      {z}{i < Math.min(agent.zones.length, 3) - 1 ? ' ·' : ''}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4">
                {(showPhone || showWhatsapp) && agent.phone && (
                  <>
                    {showPhone && (
                      <a href={`tel:${agent.phone}`}
                        className="flex items-center justify-center gap-3 px-8 py-4 text-white text-sm font-medium rounded-full transition-all hover:scale-105 shadow-md"
                        style={{ backgroundColor: '#4A7C59' }}>
                        <Phone size={18} />
                        {agent.phone}
                      </a>
                    )}
                    {showWhatsapp && (
                      <a href={`https://wa.me/${agent.phone.replace(/[^0-9]/g, '')}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center justify-center gap-3 px-8 py-4 bg-[#25D366] hover:bg-[#1fb855] text-white text-sm font-medium rounded-full transition-all hover:scale-105 shadow-md">
                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        WhatsApp
                      </a>
                    )}
                  </>
                )}
              </div>

              {/* Social */}
              {agent.social_media && Object.values(agent.social_media).some(v => v) && (
                <div className="flex gap-4">
                  {agent.social_media.facebook && (
                    <a href={agent.social_media.facebook} target="_blank" rel="noopener noreferrer"
                      className="p-2 rounded-full transition-all hover:scale-110" style={{ color: '#8B7355' }}>
                      <Facebook size={18} />
                    </a>
                  )}
                  {agent.social_media.instagram && (
                    <a href={agent.social_media.instagram} target="_blank" rel="noopener noreferrer"
                      className="p-2 rounded-full transition-all hover:scale-110" style={{ color: '#8B7355' }}>
                      <Instagram size={18} />
                    </a>
                  )}
                  {agent.social_media.linkedin && (
                    <a href={agent.social_media.linkedin} target="_blank" rel="noopener noreferrer"
                      className="p-2 rounded-full transition-all hover:scale-110" style={{ color: '#8B7355' }}>
                      <Linkedin size={18} />
                    </a>
                  )}
                  {agent.social_media.tiktok && (
                    <a href={agent.social_media.tiktok} target="_blank" rel="noopener noreferrer"
                      className="p-2 rounded-full transition-all hover:scale-110" style={{ color: '#8B7355' }}>
                      <svg className="w-[18px] h-[18px]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                      </svg>
                    </a>
                  )}
                  {agent.social_media.youtube && (
                    <a href={agent.social_media.youtube} target="_blank" rel="noopener noreferrer"
                      className="p-2 rounded-full transition-all hover:scale-110" style={{ color: '#8B7355' }}>
                      <Youtube size={18} />
                    </a>
                  )}
                </div>
              )}
            </motion.div>

            {/* Avatar — marco natural */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 1 }}
              className="flex justify-center"
            >
              <div className="relative">
                {/* Marco orgánico */}
                <div className="absolute -inset-3 rounded-[40%_60%_60%_40%/40%_40%_60%_60%] opacity-30"
                  style={{ backgroundColor: '#4A7C59' }} />
                {agent.avatar_url ? (
                  <img
                    src={agent.avatar_url}
                    alt={agent.name}
                    className="relative w-72 h-72 lg:w-80 lg:h-80 object-cover shadow-xl"
                    style={{ borderRadius: '40% 60% 60% 40% / 40% 40% 60% 60%' }}
                  />
                ) : (
                  <div className="relative w-72 h-72 lg:w-80 lg:h-80 flex items-center justify-center"
                    style={{ borderRadius: '40% 60% 60% 40% / 40% 40% 60% 60%', backgroundColor: '#D4C5A9' }}>
                    <Leaf size={80} style={{ color: '#4A7C59', opacity: 0.4 }} />
                  </div>
                )}
                {/* Badge propiedades */}
                <motion.div
                  animate={{ y: [-6, 6, -6] }}
                  transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                  className="absolute -bottom-4 -right-4 px-4 py-3 rounded-2xl shadow-lg"
                  style={{ backgroundColor: '#4A7C59' }}
                >
                  <div className="text-center">
                    <div className="text-2xl font-light text-white">{properties.length}</div>
                    <div className="text-xs text-green-200">propiedades</div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-12 border-y" style={{ borderColor: '#D4C5A9', backgroundColor: '#EDE8DC' }}>
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid grid-cols-3 gap-8 text-center">
            {[
              { icon: Leaf, label: 'Sustentabilidad', desc: 'Propiedades con conciencia ambiental' },
              { icon: Sun, label: 'Calidad de vida', desc: 'Espacios que nutren el bienestar' },
              { icon: Wind, label: 'Autenticidad', desc: 'Sin artificios, solo lugares reales' },
            ].map(({ icon: Icon, label, desc }, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15 }}
                viewport={{ once: true }}
                className="space-y-2"
              >
                <Icon size={28} className="mx-auto" style={{ color: '#4A7C59' }} />
                <h3 className="font-medium text-sm" style={{ color: '#3D3228' }}>{label}</h3>
                <p className="text-xs font-light" style={{ color: '#8B7355' }}>{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Propiedades */}
      {properties.length > 0 && (
        <section className="py-24">
          <div className="max-w-6xl mx-auto px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="mb-4"
            >
              <div className="flex items-center gap-3 mb-2">
                <Leaf size={20} style={{ color: '#4A7C59' }} />
                <span className="text-xs tracking-widest uppercase" style={{ color: '#4A7C59' }}>Portafolio</span>
              </div>
              <h2 className="text-4xl font-light" style={{ color: '#3D3228' }}>Espacios para vivir</h2>

              <PropertyFilters
                filters={filters}
                onFiltersChange={setFilters}
                propertyTypes={propertyTypes}
                totalProperties={properties.length}
                filteredCount={filteredProperties.length}
                colorSchemeKey="green"
                showFilters={showFilters}
                onToggleFilters={() => setShowFilters(v => !v)}
              />
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-10">
              {paginatedProperties.map((property, index) => (
                <motion.article
                  key={property.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.7 }}
                  viewport={{ once: true }}
                  onClick={() => handlePropertyClick(property.seo_title)}
                  className="group cursor-pointer rounded-3xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-500"
                  style={{ backgroundColor: '#EDE8DC', border: '1px solid #D4C5A9' }}
                >
                  <div className="relative h-56 overflow-hidden">
                    {property.featured_image ? (
                      <img
                        src={property.featured_image}
                        alt={property.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center"
                        style={{ backgroundColor: '#D4C5A9' }}>
                        <Leaf size={48} style={{ color: '#4A7C59', opacity: 0.4 }} />
                      </div>
                    )}
                    <div className="absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-medium text-white"
                      style={{ backgroundColor: '#4A7C59' }}>
                      {property.purpose}
                    </div>
                  </div>
                  <div className="p-6 space-y-3">
                    <h3 className="font-medium text-lg group-hover:opacity-70 transition-opacity line-clamp-1"
                      style={{ color: '#3D3228' }}>
                      {property.title}
                    </h3>
                    <p className="text-sm flex items-center gap-1 font-light" style={{ color: '#8B7355' }}>
                      <MapPin size={13} />
                      {property.zone}{property.city ? `, ${property.city}` : ''}
                    </p>
                    {property.description && (
                      <p className="text-sm font-light line-clamp-2" style={{ color: '#6B5744' }}>
                        {property.description}
                      </p>
                    )}
                    <div className="flex items-center justify-between pt-3"
                      style={{ borderTop: '1px solid #D4C5A9' }}>
                      <div className="flex items-center gap-3 text-xs" style={{ color: '#8B7355' }}>
                        {property.bedrooms && <span className="flex items-center gap-1"><Bed size={13} /> {property.bedrooms}</span>}
                        {property.bathrooms && <span className="flex items-center gap-1"><Bath size={13} /> {property.bathrooms}</span>}
                        {property.half_bathrooms > 0 && <span className="flex items-center gap-1"><Toilet size={13} /> {property.half_bathrooms}</span>}
                        {property.area_m2 && <span className="flex items-center gap-1"><Maximize size={13} /> {property.area_m2}m²</span>}
                      </div>
                      <span className="text-sm font-medium" style={{ color: '#4A7C59' }}>
                        {formatPrice(property.price, property.currency)}
                      </span>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>

            <PropertyPagination
              currentPage={currentPage}
              totalPages={totalPages}
              perPage={perPage}
              onPageChange={setCurrentPage}
              onPerPageChange={setPerPage}
              colorSchemeKey="green"
            />
          </div>
        </section>
      )}

      {/* Especialidades */}
      {agent.specialties?.length > 0 && (
        <section className="py-20" style={{ backgroundColor: '#EDE8DC' }}>
          <div className="max-w-6xl mx-auto px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="flex items-center gap-3 mb-2">
                <Leaf size={20} style={{ color: '#4A7C59' }} />
                <span className="text-xs tracking-widest uppercase" style={{ color: '#4A7C59' }}>Expertise</span>
              </div>
              <h2 className="text-4xl font-light mb-12" style={{ color: '#3D3228' }}>Especialidades</h2>
              <div className="flex flex-wrap gap-3">
                {agent.specialties.map((specialty, index) => (
                  <motion.span
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.07 }}
                    viewport={{ once: true }}
                    className="px-6 py-3 rounded-full text-sm font-light border transition-all hover:scale-105"
                    style={{ borderColor: '#4A7C59', color: '#4A7C59', backgroundColor: 'transparent' }}
                  >
                    {specialty}
                  </motion.span>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 relative overflow-hidden" style={{ backgroundColor: '#3D3228' }}>
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <svg viewBox="0 0 800 400" className="w-full h-full">
            <circle cx="700" cy="200" r="300" fill="#4A7C59" />
            <circle cx="100" cy="300" r="200" fill="#7BAF8A" />
          </svg>
        </div>
        <div className="relative max-w-6xl mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto text-center space-y-8"
          >
            <Leaf size={40} className="mx-auto" style={{ color: '#7BAF8A' }} />
            <h2 className="text-4xl lg:text-5xl font-light text-white leading-tight">
              Encuentra tu espacio natural
            </h2>
            <p className="text-lg font-light" style={{ color: '#A89880' }}>
              Lugares donde vivir de verdad. {properties.length > 0 && `${properties.length} propiedades esperándote.`}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {agent.phone && (
                <>
                  <a href={`tel:${agent.phone}`}
                    className="flex items-center justify-center gap-3 px-8 py-4 text-white rounded-full font-medium transition-all hover:scale-105 shadow-lg"
                    style={{ backgroundColor: '#4A7C59' }}>
                    <Phone size={18} /> Llamar
                  </a>
                  <a href={`https://wa.me/${agent.phone.replace(/[^0-9]/g, '')}`}
                    target="_blank" rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 px-8 py-4 bg-[#25D366] hover:bg-[#1fb855] text-white rounded-full font-medium transition-all hover:scale-105 shadow-lg">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </a>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <ChatWidget
        agentName={agent.name}
        agentSubdomain={window.location.hostname.split('.')[0]}
        agentAvatar={agent.avatar_url}
        agentPlan={agent.plan}
      />
    </div>
  )
}

export default NaturalTemplate
